import {
  BRIDGE_COMMANDS,
  BRIDGE_EVENTS,
  BRIDGE_VERSION,
  BridgeError,
  BridgeInfo,
  type BridgeArgs,
  type BridgeCommand,
  type BridgeEvent,
  type BridgeEventPayload,
  type BridgeResult,
} from '@boardflight/shared';

/**
 * The desktop bridge, from the page's side (`packages/shared/src/board-link.ts`, contract v1).
 *
 * The desktop app's window loads this same site and grants it one thing: the `bf_*` commands and
 * the `bf:*` events. This file is the only place that touches them. It calls a command exactly as
 * the contract writes it — one object of arguments, checked against the command's own schema
 * before it leaves — and hands back exactly what the contract says comes back, checked again on
 * the way in. A page that got an answer of the wrong shape from an app newer or older than itself
 * should find out here, in one sentence, and not three components later.
 *
 * **Nothing in here can reach a Board, read a Board's token, or hold a refresh token.** There is
 * no command that returns either, and the page never asks the native side to fetch anything.
 */

/** The shell's `invoke`: one command name, one object of arguments. */
export type Invoke = (command: string, args?: Record<string, unknown>) => Promise<unknown>;
/** The shell's `listen`: resolves with the function that stops listening. */
export type Listen = (event: string, handler: (event: { payload: unknown }) => void) => Promise<() => void>;

export interface Shell {
  invoke: Invoke;
  listen: Listen | null;
}

/**
 * The shell this page is running in, or null in an ordinary browser.
 *
 * The contract says `invoke(command, args)` and not where `invoke` is found. This reads it where
 * the shell the desktop app grew out of puts it — Tauri's page global — and nowhere else, so the
 * day that changes it changes here.
 */
export function findShell(scope: unknown = globalThis): Shell | null {
  const tauri = (scope as { __TAURI__?: { core?: { invoke?: unknown }; event?: { listen?: unknown } } } | null)?.__TAURI__;
  const invoke = tauri?.core?.invoke;
  if (typeof invoke !== 'function') return null;
  const listen = tauri?.event?.listen;
  return { invoke: invoke as Invoke, listen: typeof listen === 'function' ? (listen as Listen) : null };
}

/**
 * A command that did not go through. `code` is the contract's and is what a page branches on;
 * `boardMessage` is Board's own text whenever Board is what refused, and is shown as it came.
 */
export class BridgeFailure extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly boardMessage: string | null = null,
    readonly httpStatus: number | null = null,
  ) {
    super(message);
    this.name = 'BridgeFailure';
  }
}

/** Whatever a rejected `invoke` carried, as a {@link BridgeFailure}. */
function failureOf(reason: unknown): BridgeFailure {
  if (reason instanceof BridgeFailure) return reason;
  const parsed = BridgeError.safeParse(reason);
  if (parsed.success) {
    return new BridgeFailure(parsed.data.code, parsed.data.message, parsed.data.boardMessage ?? null, parsed.data.httpStatus ?? null);
  }
  // A shell with no such command rejects with a sentence of its own rather than the contract's
  // error. To the page that is the contract's `unsupported`, whatever the sentence says.
  return new BridgeFailure('unsupported', typeof reason === 'string' ? reason : 'BoardFlight could not do that.');
}

export interface Bridge {
  info: BridgeInfo;
  /** Whether the app on the other side names this capability. */
  has(capability: string): boolean;
  /** One command, as the contract defines it. Rejects with a {@link BridgeFailure}. */
  call<C extends BridgeCommand>(command: C, args: BridgeArgs<C>): Promise<BridgeResult<C>>;
  /** One event. Returns the function that stops listening; a payload of the wrong shape is dropped. */
  on<E extends BridgeEvent>(event: E, handler: (payload: BridgeEventPayload<E>) => void): () => void;
}

/** A bridge over a shell whose `bf_bridge_info` has already answered with `info`. */
export function bridgeOver(shell: Shell, info: BridgeInfo): Bridge {
  const capabilities = new Set(info.capabilities);
  return {
    info,
    has: (capability) => capabilities.has(capability),

    async call(command, args) {
      const contract = BRIDGE_COMMANDS[command];
      const sent = contract.args.safeParse(args);
      // Refused here, before it crosses: the native side checks too, but an argument the contract
      // forbids is this page's mistake and should read as one.
      if (!sent.success) throw new BridgeFailure('invalid_args', `${command}: ${sent.error.issues[0]?.message ?? 'bad arguments'}`);
      let answer: unknown;
      try {
        answer = await shell.invoke(command, sent.data as Record<string, unknown>);
      } catch (reason) {
        throw failureOf(reason);
      }
      // "Returns nothing" arrives as null from one shell and undefined from another.
      const received = contract.result.safeParse(answer === undefined ? null : answer);
      if (!received.success) throw new BridgeFailure('error', `${command} answered with something this page does not understand.`);
      return received.data as BridgeResult<typeof command>;
    },

    on(event, handler) {
      if (!shell.listen) return () => undefined;
      let stopped = false;
      let stop: (() => void) | null = null;
      void shell
        .listen(event, (raw) => {
          if (stopped) return;
          const payload = BRIDGE_EVENTS[event].safeParse(raw.payload === undefined ? null : raw.payload);
          if (payload.success) handler(payload.data as BridgeEventPayload<typeof event>);
        })
        .then(
          (unlisten) => {
            if (stopped) unlisten();
            else stop = unlisten;
          },
          () => undefined,
        );
      return () => {
        stopped = true;
        stop?.();
      };
    },
  };
}

export type BridgeConnection =
  /** An ordinary browser, or a shell that is not the desktop app. */
  | { kind: 'none' }
  /** The desktop app, speaking a contract version this page cannot use. Say "Update BoardFlight". */
  | { kind: 'outdated'; info: BridgeInfo }
  | { kind: 'connected'; bridge: Bridge };

/**
 * Ask who is on the other side — the first call a page makes, and the only one it may assume
 * exists. A shell that does not answer `bf_bridge_info` with the contract's shape is not the
 * desktop app as far as this page is concerned, and nothing else is ever called on it.
 */
export async function connectBridge(shell: Shell | null): Promise<BridgeConnection> {
  if (!shell) return { kind: 'none' };
  let info: BridgeInfo;
  try {
    const parsed = BridgeInfo.safeParse(await shell.invoke('bf_bridge_info', {}));
    if (!parsed.success) return { kind: 'none' };
    info = parsed.data;
  } catch {
    return { kind: 'none' };
  }
  // The version moves only for a change one side would get wrong (`BRIDGE_VERSION`), so a
  // different one is not something to feature-detect around.
  if (info.bridge !== BRIDGE_VERSION) return { kind: 'outdated', info };
  return { kind: 'connected', bridge: bridgeOver(shell, info) };
}
